(() => {
  if (customElements.get('subscription-purchase-options')) return;

  customElements.define(
    'subscription-purchase-options',
    class SubscriptionPurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.variantData = this.readVariantData();
        this.initialSubscriptionSelectionHandled = false;
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('quantity-price:update', this.handleQuantityPriceUpdate);
        this.closest('.shopify-section')?.addEventListener('variant:update', this.handleVariantUpdate);

        this.hideExternalSubscriptionWidget();
        this.observeExternalSubscriptionWidget();
        window.requestAnimationFrame(() => this.sync());
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('quantity-price:update', this.handleQuantityPriceUpdate);
        this.closest('.shopify-section')?.removeEventListener('variant:update', this.handleVariantUpdate);
        this.form?.removeEventListener('submit', this.handleFormSubmit, true);
        this.externalWidgetObserver?.disconnect();
      }

      readVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-subscription-variants]')?.textContent || '{}');
        } catch (_error) {
          return {};
        }
      }

      refreshFormRefs() {
        const scope = this.closest('.product-details') || this.closest('.shopify-section') || document;
        const productForm = scope.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
        const form = productForm?.querySelector('form');

        this.variantInput = productForm?.querySelector('input[name="id"]') || null;

        if (form && form !== this.form) {
          this.form?.removeEventListener('submit', this.handleFormSubmit, true);
          this.form = form;
          this.form.addEventListener('submit', this.handleFormSubmit, true);
        }
      }

      handleFormSubmit = () => {
        this.sync();
      };

      handleClick = (event) => {
        const subscriptionCard = event.target?.closest?.('.subscription-options__card--subscription');
        if (!subscriptionCard || subscriptionCard.classList.contains('is-disabled')) return;

        const subscriptionInput = subscriptionCard.querySelector('input[value="subscription"]');
        if (!subscriptionInput || subscriptionInput.disabled || subscriptionInput.checked) return;

        subscriptionInput.checked = true;
        subscriptionInput.dispatchEvent(new Event('change', { bubbles: true }));
      };

      handleChange = (event) => {
        if (event.target?.name?.startsWith('selling_plan_choice_')) {
          const subscriptionInput = this.querySelector('input[value="subscription"]');
          if (subscriptionInput && !subscriptionInput.disabled) subscriptionInput.checked = true;
        }

        if (event.target?.name?.startsWith('purchase_option_')) {
          if (event.target.value === 'one-time') {
            this.clearSubscriptionRedirectParam();
          } else if (event.target.value === 'subscription') {
            this.applySubscriptionRedirectParam();
          }
        }

        this.sync();
      };

      handleQuantityPriceUpdate = (event) => {
        this.quantityPriceOverride = event.detail?.price || '';
        this.quantityPriceOverrideCents = Number(event.detail?.priceCents) || 0;
        this.quantityEachBox = Number(event.detail?.eachBox) || 0;
        this.sync();
      };

      handleVariantUpdate = (event) => {
        if (event.detail?.data?.productId !== this.dataset.productId) return;

        const replacement = event.detail.data.html.querySelector(`subscription-purchase-options[id="${this.id}"]`);
        if (replacement) {
          const nextFieldset = replacement.querySelector('.subscription-options__fieldset');
          const nextVariantData = replacement.querySelector('[data-subscription-variants]');
          if (nextFieldset) this.querySelector('.subscription-options__fieldset')?.replaceWith(nextFieldset);
          if (nextVariantData) this.querySelector('[data-subscription-variants]')?.replaceWith(nextVariantData);
          this.variantData = this.readVariantData();
        }

        this.hideExternalSubscriptionWidget();
        window.requestAnimationFrame(() => this.sync());
      };

      hideExternalSubscriptionWidget() {
        const productDetails = this.closest('.product-details');
        productDetails?.querySelectorAll('[data-widget-container-wrapper]').forEach((widget) => {
          if (this.contains(widget)) return;
          widget.setAttribute('hidden', '');
          widget.setAttribute('aria-hidden', 'true');
          widget.querySelectorAll('input, select, textarea, button').forEach((control) => {
            control.disabled = true;
          });
        });
      }

      observeExternalSubscriptionWidget() {
        const productDetails = this.closest('.product-details');
        if (!productDetails || this.externalWidgetObserver) return;

        this.externalWidgetObserver = new MutationObserver(() => this.hideExternalSubscriptionWidget());
        this.externalWidgetObserver.observe(productDetails, { childList: true, subtree: true });
      }

      getCurrentVariantData() {
        this.refreshFormRefs();
        return this.variantData[this.variantInput?.value] || Object.values(this.variantData)[0] || { plans: [] };
      }

      getSelectedPlan() {
        const variant = this.getCurrentVariantData();
        const selectedPlanId = this.querySelector('input[name^="selling_plan_choice_"]:checked')?.value;
        return variant.plans.find((plan) => String(plan.id) === String(selectedPlanId)) || variant.plans[0];
      }

      shouldForceSubscriptionSelection() {
        const url = new URL(window.location.href);
        const subscribeParam = url.searchParams.get('subscribe');
        const hash = url.hash.replace(/^#/, '');
        return subscribeParam === 'true' && hash === 'subscription-purchase-options';
      }

      forceSubscriptionSelectionIfRequested(hasPlans) {
        if (this.initialSubscriptionSelectionHandled || !hasPlans || !this.shouldForceSubscriptionSelection()) return;

        const subscriptionInput = this.querySelector('input[value="subscription"]');
        this.initialSubscriptionSelectionHandled = true;

        if (!subscriptionInput || subscriptionInput.disabled) return;

        if (!subscriptionInput.checked) {
          subscriptionInput.checked = true;
        }

        this.applySubscriptionRedirectParam();
      }

      applySubscriptionRedirectParam() {
        const url = new URL(window.location.href);
        url.searchParams.set('subscribe', 'true');
        window.history.replaceState({}, '', url.toString());
      }

      clearSubscriptionRedirectParam() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('subscribe')) return;

        url.searchParams.delete('subscribe');
        window.history.replaceState({}, '', url.toString());
      }

      sync() {
        this.refreshFormRefs();

        const variant = this.getCurrentVariantData();
        const subscriptionInput = this.querySelector('input[value="subscription"]');
        const hasPlans = variant.plans.length > 0;
        this.forceSubscriptionSelectionIfRequested(hasPlans);
        const selectedType = this.querySelector('input[name^="purchase_option_"]:checked')?.value || 'one-time';

        if (subscriptionInput) subscriptionInput.disabled = !hasPlans;
        this.querySelector('.subscription-options__card--subscription')?.classList.toggle('is-disabled', !hasPlans);

        if (selectedType === 'subscription' && !hasPlans) {
          this.querySelector('input[value="one-time"]')?.click();
          return;
        }

        const selectedPlan = this.getSelectedPlan();
        const quantitySubscriptionPrice = this.getQuantitySubscriptionPrice(variant, selectedPlan);
        const selectedPrice =
          selectedType === 'subscription'
            ? quantitySubscriptionPrice || selectedPlan?.price
            : this.quantityPriceOverride || variant.oneTimePrice;
        const comparePrice =
          selectedType === 'subscription' ? this.quantityPriceOverride || variant.oneTimePrice : '';

        this.updateDisplayedPrices(variant, selectedPlan);
        this.updateSavingsText(variant, selectedPlan);
        this.updateHiddenSellingPlan(selectedType === 'subscription' ? selectedPlan?.id : '');
        this.updatePrices(selectedPrice, comparePrice);
      }

      updateDisplayedPrices(variant, plan) {
        const oneTimePrice = this.querySelector('[data-one-time-price]');
        const subscriptionPrice = this.querySelector('[data-subscription-price]');
        const subscriptionComparePrice = this.querySelector('[data-subscription-compare-price]');
        const subscriptionEachPrice = this.querySelector('[data-subscription-each-price]');
        const subscriptionEachPriceMobile = this.querySelector('[data-subscription-each-price-mobile]');

        const quantityPrice = this.quantityPriceOverride;
        const quantitySubscriptionPrice = this.getQuantitySubscriptionPrice(variant, plan);
        const quantitySubscriptionEachPrice = this.getQuantitySubscriptionEachPrice(variant, plan);

        if (oneTimePrice && (quantityPrice || variant.oneTimePrice)) {
          oneTimePrice.textContent = quantityPrice || variant.oneTimePrice;
        }
        if (subscriptionPrice && (quantitySubscriptionPrice || plan?.price)) {
          subscriptionPrice.textContent = quantitySubscriptionPrice || plan.price;
        }
        if (subscriptionComparePrice) {
          subscriptionComparePrice.textContent = quantityPrice || plan?.compareAtPrice ? quantityPrice || plan.compareAtPrice : '';
        }
        if (subscriptionEachPrice) {
          subscriptionEachPrice.textContent = quantitySubscriptionEachPrice || plan?.eachPrice ? `(${quantitySubscriptionEachPrice || plan.eachPrice} / EA)` : '';
        }
        if (subscriptionEachPriceMobile) {
          subscriptionEachPriceMobile.textContent = quantitySubscriptionEachPrice || plan?.eachPrice ? `${quantitySubscriptionEachPrice || plan.eachPrice} / EA` : '';
        }
      }

      updateSavingsText(variant, plan) {
        const oneTimePriceCents = this.quantityPriceOverrideCents || variant?.oneTimePriceCents || 0;
        const subscriptionPriceCents = this.getQuantitySubscriptionPriceCents(variant, plan) || plan?.priceCents || 0;
        const savingsPrice = this.formatMoney(Math.max(oneTimePriceCents - subscriptionPriceCents, 0));

        this.querySelectorAll('[data-subscription-benefit-template]').forEach((benefit) => {
          const template = benefit.dataset.subscriptionBenefitTemplate || benefit.textContent || '';
          benefit.textContent = template.replace(/{\s*amount\s*}/g, savingsPrice);
        });
      }

      getQuantitySubscriptionPrice(variant, plan) {
        const priceCents = this.getQuantitySubscriptionPriceCents(variant, plan);
        return priceCents ? this.formatMoney(priceCents) : '';
      }

      getQuantitySubscriptionEachPrice(variant, plan) {
        const priceCents = this.getQuantitySubscriptionPriceCents(variant, plan);
        const divisor = this.quantityEachBox || 12;
        return priceCents ? this.formatMoney(Math.round(priceCents / divisor)) : '';
      }

      getQuantitySubscriptionPriceCents(variant, plan) {
        if (!this.quantityPriceOverrideCents || !variant?.oneTimePriceCents || !plan?.priceCents) return 0;
        return Math.round((this.quantityPriceOverrideCents * plan.priceCents) / variant.oneTimePriceCents);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const addThousands = (value) => value.replace(/\d(?=(\d{3})+(?!\d))/g, '$&,');
        const amount = addThousands((cents / 100).toFixed(2));
        const amountNoDecimals = addThousands(Math.round(cents / 100).toString());
        const amountWithCommaSeparator = amount.replace('.', ',');
        const amountNoDecimalsWithCommaSeparator = amountNoDecimals;

        return format.replace(/{{\s*(\w+)\s*}}/g, (_match, placeholder) => {
          switch (placeholder) {
            case 'amount':
              return amount;
            case 'amount_no_decimals':
              return amountNoDecimals;
            case 'amount_with_comma_separator':
              return amountWithCommaSeparator;
            case 'amount_no_decimals_with_comma_separator':
              return amountNoDecimalsWithCommaSeparator;
            case 'currency':
              return this.dataset.currency || '';
            default:
              return amount;
          }
        });
      }

      updateHiddenSellingPlan(planId) {
        this.refreshFormRefs();
        if (!this.form) return;

        this.form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          if (input.dataset.subscriptionOptionsInput === 'true') return;
          input.disabled = true;
        });

        let input = this.form.querySelector('input[name="selling_plan"][data-subscription-options-input="true"]');
        if (planId) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'selling_plan';
            input.dataset.subscriptionOptionsInput = 'true';
            this.form.appendChild(input);
          }
          input.disabled = false;
          input.value = planId;
        } else {
          input?.remove();
        }
      }

      updatePrices(price, comparePrice) {
        if (!price) return;
        const scope = this.closest('.shopify-section') || document;
        scope.querySelectorAll('[data-atc-price-wrapper]').forEach((target) => {
          this.renderPrice(target, price, comparePrice);
        });

        const livePriceTargets = [
          '.PDP_price_detail_main_new product-price [ref="priceContainer"]',
          '.PDP_nutrition_innerpopup_innerside_innerblock_detail_new product-price [ref="priceContainer"]',
        ];

        livePriceTargets.forEach((selector) => {
          const priceTarget = scope.querySelector(selector);
          if (priceTarget) this.renderPrice(priceTarget, price, comparePrice);
        });
      }

      renderPrice(target, price, comparePrice) {
        target.textContent = '';

        if (comparePrice && comparePrice !== price) {
          const compare = document.createElement('span');
          compare.className = 'price-item__group subscription-options__compare-price';

          const compareText = document.createElement('s');
          compareText.className = 'compare-at-price';
          compareText.textContent = comparePrice;
          compare.appendChild(compareText);

          const sale = document.createElement('span');
          sale.className = 'price-item__group subscription-options__sale-price';
          sale.textContent = price;

          target.append(sale, document.createTextNode(' '), compare);
          return;
        }

        target.textContent = price;
      }
    }
  );
})();
